/**
 * Smart anchored-tooltip positioning for the guided tour.
 *
 * The instruction card "orbits" the highlighted target and must never sit on
 * top of it. We try a prioritised list of placements (below / above / right /
 * left), clamp each candidate inside the safe viewport, reject any that overlap
 * the target, and fall back to the least-bad position (shrinking width if
 * needed) when nothing fits cleanly.
 */

export const TOOLTIP_GAP = 12;
export const VIEWPORT_MARGIN = 14; // within the requested 12–16px range
export const MIN_TOOLTIP_WIDTH = 240;

function clamp(value, min, max) {
  if (max < min) return min;
  return Math.min(Math.max(value, min), max);
}

function rect(left, top, width, height) {
  return { left, top, width, height, right: left + width, bottom: top + height };
}

function overlapArea(a, b) {
  const x = Math.max(0, Math.min(a.right, b.right) - Math.max(a.left, b.left));
  const y = Math.max(0, Math.min(a.bottom, b.bottom) - Math.max(a.top, b.top));
  return x * y;
}

function offscreenAmount(box, bounds) {
  return (
    Math.max(0, bounds.minX - box.left)
    + Math.max(0, box.right - bounds.maxX)
    + Math.max(0, bounds.minY - box.top)
    + Math.max(0, box.bottom - bounds.maxY)
  );
}

/**
 * Build a single candidate for a placement, clamped into the safe bounds.
 */
function buildCandidate(placement, target, width, height, bounds) {
  const gap = TOOLTIP_GAP;
  const cx = target.left + target.width / 2;
  const cy = target.top + target.height / 2;

  let left;
  let top;
  if (placement === 'bottom') {
    top = target.bottom + gap;
    left = cx - width / 2;
  } else if (placement === 'top') {
    top = target.top - gap - height;
    left = cx - width / 2;
  } else if (placement === 'right') {
    left = target.right + gap;
    top = cy - height / 2;
  } else {
    left = target.left - gap - width;
    top = cy - height / 2;
  }

  left = clamp(left, bounds.minX, bounds.maxX - width);
  top = clamp(top, bounds.minY, bounds.maxY - height);
  return { ...rect(left, top, width, height), placement };
}

function arrowFor(placement, box, target) {
  // Arrow points from the card toward the target.
  if (placement === 'bottom') {
    const center = target.left + target.width / 2;
    return { side: 'top', offset: clamp(center - box.left, 16, box.width - 16) };
  }
  if (placement === 'top') {
    const center = target.left + target.width / 2;
    return { side: 'bottom', offset: clamp(center - box.left, 16, box.width - 16) };
  }
  if (placement === 'right') {
    const center = target.top + target.height / 2;
    return { side: 'left', offset: clamp(center - box.top, 16, box.height - 16) };
  }
  const center = target.top + target.height / 2;
  return { side: 'right', offset: clamp(center - box.top, 16, box.height - 16) };
}

/**
 * Preferred placement order based on where the target sits in the viewport.
 *  - top half  → below first; bottom half → above first
 *  - left side → right before left; right side → left before right
 */
function placementOrder(target, viewport) {
  const cy = target.top + target.height / 2;
  const cx = target.left + target.width / 2;
  const verticalPrimary = cy < viewport.height / 2 ? 'bottom' : 'top';
  const verticalSecondary = verticalPrimary === 'bottom' ? 'top' : 'bottom';
  const horizontalPrimary = cx < viewport.width / 2 ? 'right' : 'left';
  const horizontalSecondary = horizontalPrimary === 'right' ? 'left' : 'right';
  return [verticalPrimary, horizontalPrimary, verticalSecondary, horizontalSecondary];
}

/**
 * @param {object} args
 * @param {DOMRect|object} args.targetRect highlighted element rect (viewport coords)
 * @param {{width:number,height:number}} args.card measured card size
 * @param {{width:number,height:number}} args.viewport
 * @param {{top:number,right:number,bottom:number,left:number}} [args.safeInsets]
 * @returns {{top:number,left:number,width:number,placement:string,arrow:object,fits:boolean}}
 */
export function computeTooltipPlacement({
  targetRect,
  card,
  viewport,
  safeInsets = { top: 0, right: 0, bottom: 0, left: 0 },
}) {
  const bounds = {
    minX: VIEWPORT_MARGIN + safeInsets.left,
    maxX: viewport.width - VIEWPORT_MARGIN - safeInsets.right,
    minY: VIEWPORT_MARGIN + safeInsets.top,
    maxY: viewport.height - VIEWPORT_MARGIN - safeInsets.bottom,
  };

  const availW = Math.max(MIN_TOOLTIP_WIDTH, bounds.maxX - bounds.minX);
  const height = card.height || 200;
  const order = placementOrder(targetRect, viewport);

  // Pass 1: full width, find first candidate with no overlap that stays on-screen.
  const fullWidth = Math.min(card.width, availW);
  for (let i = 0; i < order.length; i += 1) {
    const box = buildCandidate(order[i], targetRect, fullWidth, height, bounds);
    if (overlapArea(box, targetRect) === 0 && offscreenAmount(box, bounds) < 1) {
      return {
        top: box.top,
        left: box.left,
        width: fullWidth,
        placement: order[i],
        arrow: arrowFor(order[i], box, targetRect),
        fits: true,
      };
    }
  }

  // Pass 2: shrink width and retry (helps the left/right placements fit).
  const narrowWidth = Math.max(MIN_TOOLTIP_WIDTH, Math.min(fullWidth, availW * 0.8));
  for (let i = 0; i < order.length; i += 1) {
    const box = buildCandidate(order[i], targetRect, narrowWidth, height, bounds);
    if (overlapArea(box, targetRect) === 0 && offscreenAmount(box, bounds) < 1) {
      return {
        top: box.top,
        left: box.left,
        width: narrowWidth,
        placement: order[i],
        arrow: arrowFor(order[i], box, targetRect),
        fits: true,
      };
    }
  }

  // Pass 3: no clean fit — pick the safest edge (least overlap, then least off-screen).
  let best = null;
  [fullWidth, narrowWidth].forEach((w) => {
    order.forEach((placement) => {
      const box = buildCandidate(placement, targetRect, w, height, bounds);
      const penalty = overlapArea(box, targetRect) * 10000 + offscreenAmount(box, bounds) * 100;
      if (!best || penalty < best.penalty) {
        best = { box, placement, width: w, penalty };
      }
    });
  });

  const mobile = viewport.width < 768;
  const stillOverlaps = overlapArea(best.box, targetRect) > 0;
  if (mobile && stillOverlaps) {
    return computeMobileBottomSheetPlacement({
      viewport,
      safeInsets,
      cardWidth: fullWidth,
      cardHeight: height,
      targetRect,
    });
  }

  return {
    top: best.box.top,
    left: best.box.left,
    width: best.width,
    placement: best.placement,
    arrow: arrowFor(best.placement, best.box, targetRect),
    fits: best.penalty === 0,
  };
}

/**
 * Mobile PWA fallback — dock the instruction card above the bottom nav / safe area
 * so coach marks never cover the highlighted target.
 */
export function computeMobileBottomSheetPlacement({
  viewport,
  safeInsets = { top: 0, right: 0, bottom: 0, left: 0 },
  cardWidth = 340,
  cardHeight = 220,
  targetRect = null,
}) {
  const bounds = {
    minX: VIEWPORT_MARGIN + safeInsets.left,
    maxX: viewport.width - VIEWPORT_MARGIN - safeInsets.right,
    minY: VIEWPORT_MARGIN + safeInsets.top,
    maxY: viewport.height - VIEWPORT_MARGIN - safeInsets.bottom,
  };
  const width = Math.min(cardWidth, bounds.maxX - bounds.minX);
  const left = clamp((viewport.width - width) / 2, bounds.minX, bounds.maxX - width);

  const dockBottomTop = clamp(
    viewport.height - safeInsets.bottom - VIEWPORT_MARGIN - cardHeight,
    bounds.minY,
    bounds.maxY - cardHeight,
  );

  const candidates = [{ top: dockBottomTop }];
  if (targetRect) {
    candidates.unshift({ top: targetRect.bottom + TOOLTIP_GAP });
    candidates.push({ top: targetRect.top - TOOLTIP_GAP - cardHeight });
  }

  for (let i = 0; i < candidates.length; i += 1) {
    const top = clamp(candidates[i].top, bounds.minY, bounds.maxY - cardHeight);
    const box = rect(left, top, width, cardHeight);
    if (!targetRect || overlapArea(box, targetRect) === 0) {
      return {
        top,
        left,
        width,
        placement: 'bottom-sheet',
        arrow: null,
        fits: true,
      };
    }
  }

  return {
    top: dockBottomTop,
    left,
    width,
    placement: 'bottom-sheet',
    arrow: null,
    fits: Boolean(!targetRect),
  };
}
